import { action, set, setProperties } from '@ember/object';
import Route from '@ember/routing/route';
import { schedule } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import type { Hotel, Market, Reportgroup, User } from 'frontend-utils/models';
import { REPORT_TYPE_ALL } from 'frontend-utils/utils/constants/constant-values';
import type HotelSettingsReportsController from 'lumen/controllers/hotel/settings/reports';
import type { Transition } from 'lumen/services/transition';

type Model = {
  isLoading?: boolean;
  page: number;
  reportgroups?: Reportgroup[];
  totalPages: number;
  totalResults: number;
};

type Params = {
  hotelId?: string;
  impersonateId?: string;
  marketId?: string;
  ownReports: unknown;
  page: number;
  recipient?: string;
  sortBy: string;
  sortDirection: string;
  type: string;
};

export default class HotelSettingsReportsRoute extends Route {
  queryParams = {
    hotelId: {
      refreshModel: true,
    },
    impersonateId: {
      refreshModel: true,
    },
    marketId: {
      refreshModel: true,
    },
    ownReports: {
      refreshModel: true,
    },
    page: {
      refreshModel: true,
    },
    recipient: {
      refreshModel: true,
    },
    sortBy: {
      refreshModel: true,
    },
    sortDirection: {
      refreshModel: true,
    },
    type: {
      refreshModel: true,
    },
  };

  @service declare access: Services['access'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  fetchOptions = true;
  keepModel = false;

  activate(): void {
    this.fetchOptions = true;
  }

  beforeModel(transition: Transition): void {
    if (!this.access.hasFeature('reports.view.settings')) {
      if (transition.to?.name.includes('market')) {
        if (this.access.hasFeature('settings.hotel_access.edit')) {
          this.router.transitionTo('market.settings.hotels');
        } else if (this.access.hasFeature('spider.settings.portfolio.edit')) {
          this.router.transitionTo('market.settings.spider');
        } else if (this.access.hasPersonalSettingsTab) {
          this.router.transitionTo('market.settings.personal');
        }
      } else {
        this.router.transitionTo('hotel.settings');
      }
    }

    // https://github.com/emberjs/data/issues/4963
    // eslint-disable-next-line ember/no-runloop
    schedule('destroy', this, function () {
      this.store.unloadAll('reportconfig');
      this.store.unloadAll('reportgroup');
    });
  }

  /**
   * Necessary for fetching the selected user, hotel and/or market.
   * These are not present when visiting the route for the first time
   * @param  { object } model  current model
   * @param  { object } params  passes query params
   */

  fetchRecipientHotelMarket(model: Model, params: Params): void {
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const controller = this.controllerFor(
      'hotel.settings.reports',
    ) as HotelSettingsReportsController;

    if (params.recipient) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.store.findRecord<User>('user', params.recipient).then((user) => {
        if (user) {
          set(controller, 'selectedRecipient', {
            id: user.id,
            email: user.email,
          });
        } else {
          set(controller, 'selectedRecipient', {
            email: this.intl.t('reports.all_recipients_filter'),
          });
        }
      });
    }

    if (params.impersonateId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.store.findRecord<User>('user', params.impersonateId).then((user) => {
        if (user) {
          set(controller, 'selectedImpersonator', {
            id: user.id,
            email: user.email,
          });
        } else {
          set(controller, 'selectedImpersonator', {
            email: this.intl.t('settings.all_users_filter'),
          });
        }
      });
    }

    if (params.hotelId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.store.findRecord<Hotel>('hotel', params.hotelId).then((hotel) => {
        if (hotel) {
          set(controller, 'selectedHotelOrMarket', {
            id: hotel.id,
            label: hotel.hotelinfo?.name,
          });
        } else {
          set(controller, 'selectedHotelOrMarket', {
            label: this.intl.t('reports.all_hotels_markets_filters'),
          });
        }
      });
    }

    if (params.marketId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.store
        .findRecord<Market>('market', params.marketId)
        .then((market) => {
          if (market) {
            set(controller, 'selectedHotelOrMarket', {
              id: market.id,
              label: market.displayName,
            });
          } else {
            set(controller, 'selectedHotelOrMarket', {
              label: this.intl.t('reports.all_hotels_markets_filters'),
            });
          }
        });
    }

    set(this, 'fetchOptions', false);
  }

  model(params: Params): Model {
    const existingModel = this.modelFor('hotel.settings.reports') as
      | Model
      | undefined;
    let model: Model;

    if (this.keepModel && existingModel) {
      model = existingModel;

      setProperties(model, {
        isLoading: true,
        reportgroups: [],
        totalResults: 0,
      });

      set(this, 'keepModel', false);
    } else {
      model = {
        isLoading: true,
        page: 0,
        reportgroups: [],
        totalPages: 0,
        totalResults: 0,
      };
    }

    const queryParams: Record<string, unknown> = {
      hotel_id: params.hotelId,
      impersonate_id: params.impersonateId,
      market_id: params.marketId,
      my_reports: params.ownReports,
      page: params.page - 1,
      receiver_id: params.recipient,
      sort_by: params.sortBy,
      sort_direction: params.sortDirection,
    };

    if (params.type !== REPORT_TYPE_ALL) {
      queryParams['type'] = params.type;
    }

    // @ts-expect-error: Incorrect type
    this.store.query<Reportgroup>('reportgroup', queryParams).then(
      (content: unknown) => {
        setProperties(model, {
          isLoading: false,
          // @ts-expect-error: Incorrect type
          reportgroups: [...content],
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          totalPages: content.meta.results_num_pages + 1,
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          totalResults: content.meta.results_count,
        });

        // Can only be done after the route is visited for the first time
        if (this.fetchOptions) {
          this.fetchRecipientHotelMarket(model, params);
        }
      },
      (error) => {
        if (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          error?.errors &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          error.errors[0] &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          error.errors[0].code === 'page'
        ) {
          this.router.transitionTo({
            queryParams: {
              hotelId: params.hotelId,
              marketId: params.marketId,
              ownReports: params.ownReports,
              page: 1,
              recipient: params.recipient,
              sort_by: params.sortBy,
              sort_direction: params.sortDirection,
            },
          });
        } else {
          throw error;
        }
      },
    );

    return model;
  }

  resetController(
    controller: HotelSettingsReportsController,
    isExiting?: boolean,
  ): void {
    if (isExiting) {
      // isExiting would be false if only the route's model was changing
      set(controller, 'page', 1);

      // Unload store
      this.store.unloadAll('reportconfig');
      this.store.unloadAll('reportgroup');
    }
  }

  @action refreshRoute(): void {
    set(this, 'keepModel', true);
    this.refresh();
  }
}
